/**
 * Hook para gerenciar notificações não lidas de páginas
 *
 * Rastreia quantos itens novos existem em páginas específicas desde
 * a última visualização do usuário.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { schemaQuery } from '@/lib/api/schema-resolver';
import { useAuth } from '@/features/auth';
import { withTimeout } from '@/lib/query-client';

// Configuração das versões do changelog (hardcoded, baseado no ChangelogPage)
const CHANGELOG_VERSIONS = [
  { version: '2.2.30', date: '2025-02-01' },
  { version: '2.2.29', date: '2025-01-31' },
  { version: '2.2.28', date: '2025-01-31' },
  { version: '2.2.27', date: '2025-01-31' },
  { version: '2.2.26', date: '2025-01-30' },
  { version: '2.2.25', date: '2025-01-30' },
  { version: '2.2.24', date: '2025-01-20' },
  { version: '2.2.23', date: '2025-01-20' },
  { version: '2.2.22', date: '2025-01-19' },
  { version: '2.2.21', date: '2025-01-19' },
  { version: '2.2.20', date: '2025-01-18' },
  { version: '2.2.19', date: '2025-01-18' },
  { version: '2.2.18', date: '2025-01-17' },
  { version: '2.2.17', date: '2025-01-17' },
  { version: '2.2.16', date: '2025-01-16' },
  { version: '2.2.15', date: '2025-01-13' },
  { version: '2.2.14', date: '2025-01-13' },
  { version: '2.2.13', date: '2025-01-10' },
  { version: '2.2.12', date: '2025-01-08' },
  { version: '2.2.11', date: '2025-01-08' },
  { version: '2.2.10', date: '2025-01-08' },
  { version: '2.2.9', date: '2025-01-08' },
  { version: '2.2.8', date: '2025-01-08' },
  { version: '2.2.7', date: '2025-01-08' },
  { version: '2.2.6', date: '2025-01-08' },
  { version: '2.2.5', date: '2025-01-05' },
  { version: '2.2.4', date: '2025-12-30' },
  { version: '2.2.3', date: '2025-12-28' },
  { version: '2.2.2', date: '2025-12-24' },
  { version: '2.2.1', date: '2025-12-22' },
  { version: '2.2.0', date: '2025-12-21' },
  { version: '2.1.9', date: '2025-12-20' },
  { version: '2.1.8', date: '2025-12-20' },
  { version: '2.1.7', date: '2025-12-20' },
  { version: '2.1.6', date: '2025-12-20' },
  { version: '2.1.5', date: '2025-12-19' },
  { version: '2.1.4', date: '2025-12-19' },
  { version: '2.1.3', date: '2024-12-14' },
  { version: '2.1.2', date: '2024-12-13' },
  { version: '2.1.1', date: '2024-12-13' },
  { version: '2.1.0', date: '2024-12-12' },
  { version: '2.0.0', date: '2024-12-01' },
  { version: '1.0.0', date: '2024-11-01' },
];

interface PageViewRecord {
  page_key: string;
  last_viewed_at: string;
}

interface UnreadCounts {
  changelog: number;
  postback_errors: number;
}

export function useUnreadNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Buscar última visualização de cada página
  const { data: pageViews, isLoading: isLoadingViews } = useQuery({
    queryKey: ['user-page-views', user?.id],
    queryFn: withTimeout(async ({ signal }) => {
      if (!user?.id) return [];

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const q = schemaQuery.table('user_page_views') as any;
        const { data, error } = await q
          .select('page_key, last_viewed_at')
          .eq('user_id', user.id)
          .abortSignal(signal);

        if (error) {
          // Se der erro de permissão, retorna array vazio (usuário nunca visualizou)
          if (error.code === '42501') {
            console.warn('[useUnreadNotifications] Permission error, returning empty array');
            return [];
          }
          throw error;
        }
        return data as PageViewRecord[];
      } catch (err) {
        console.error('[useUnreadNotifications] Error fetching page views:', err);
        return [];
      }
    }),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Buscar contagem de erros de postback pendentes
  const { data: postbackErrorsCount, isLoading: isLoadingPostback } = useQuery({
    queryKey: ['postback-errors-count', user?.id],
    queryFn: withTimeout(async ({ signal }) => {
      if (!user?.id) return 0;

      // Buscar última visualização
      const lastViewed = pageViews?.find(pv => pv.page_key === 'postback_errors')?.last_viewed_at;

      // Contar erros pendentes criados após última visualização
      let query = schemaQuery
        .table('postback_error_log')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (lastViewed) {
        query = query.gt('created_at', lastViewed);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { count, error } = await (query as any).abortSignal(signal);

      if (error) throw error;
      return count || 0;
    }),
    enabled: !!user?.id && !isLoadingViews,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Calcular contagem de atualizações não lidas (changelog)
  const calculateChangelogUnread = useCallback(() => {
    if (!pageViews) return CHANGELOG_VERSIONS.length; // Se nunca visualizou, todas são novas

    const lastViewed = pageViews.find(pv => pv.page_key === 'changelog')?.last_viewed_at;

    if (!lastViewed) return CHANGELOG_VERSIONS.length;

    // Extrair apenas a data (YYYY-MM-DD) da última visualização
    const lastViewedDateStr = lastViewed.split('T')[0];

    // Contar quantas versões foram lançadas APÓS a data da última visualização
    // Se a versão foi lançada no mesmo dia ou antes, ela é considerada lida
    return CHANGELOG_VERSIONS.filter(v => {
      // Comparar strings de data diretamente (YYYY-MM-DD)
      return v.date > lastViewedDateStr;
    }).length;
  }, [pageViews]);

  // Contagem final
  const unreadCounts: UnreadCounts = {
    changelog: calculateChangelogUnread(),
    postback_errors: postbackErrorsCount || 0,
  };

  // Função para marcar página como visualizada
  const markAsViewed = useCallback(async (pageKey: 'changelog' | 'postback_errors') => {
    if (!user?.id) return;

    try {
      const { error } = await schemaQuery
        .table('user_page_views')
        .upsert({
          user_id: user.id,
          page_key: pageKey,
          last_viewed_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,page_key',
        });

      if (error) throw error;

      // Invalidar cache para atualizar contadores
      queryClient.invalidateQueries({ queryKey: ['user-page-views', user.id] });
      queryClient.invalidateQueries({ queryKey: ['postback-errors-count', user.id] });
    } catch (error) {
      console.error('Error marking page as viewed:', error);
    }
  }, [user?.id, queryClient]);

  return {
    unreadCounts,
    isLoading: isLoadingViews || isLoadingPostback,
    markAsViewed,
  };
}
